import { useFxWorkspaceContext } from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import { BtnClasses, CustomInput, Popup } from '@tcweb/connect-ui';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { Component, Show, createSignal } from 'solid-js';
import { Portal } from 'solid-js/web';

export const CreateFxWorkspaceDialog: Component<{
  show: boolean;
  onClose(): void;
}> = (props) => {
  return (
    <Show when={props.show}>
      <CreateFxWorkspaceDialogContent onClose={props.onClose} />
    </Show>
  );
};

const CreateFxWorkspaceDialogContent: Component<{
  onClose(): void;
}> = (props) => {
  const fxWorkspace = useFxWorkspaceContext();
  const [name, setName] = createSignal('');
  const [isSaving, setIsSaving] = createSignal(false);

  const handleClose = (): void => {
    if (isSaving()) {
      return;
    }
    props.onClose();
  };

  const handleCreate = async (): Promise<void> => {
    const trimmed = name().trim();
    if (!trimmed || isSaving()) {
      return;
    }

    setIsSaving(true);
    try {
      const created = await fxWorkspace.createWorkspace(trimmed);
      if (created) {
        props.onClose();
        return;
      }

      NotificationEventBus.emit({
        type: 'error',
        message: t('mobileMapping.fxWorkspace.createFailed', {
          name: trimmed,
        }),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameKeyDown = (
    event: KeyboardEvent & { currentTarget: HTMLInputElement; target: Element }
  ): void => {
    if (event.key !== 'Enter' || event.isComposing) {
      return;
    }

    event.preventDefault();
    void handleCreate();
  };

  return (
    <Portal>
      <Popup
        show={true}
        name="create-fx-workspace"
        heading={t('mobileMapping.fxWorkspace.createWorkspaceTitle')}
        role="dialog"
        aria-modal={true}
        overlayClass="z-modal"
        closeOnBackdropClick={!isSaving()}
        backdropAndCloseDisabled={isSaving()}
        content={
          <CustomInput
            name="fx-workspace-name"
            label={t('mobileMapping.fxWorkspace.nameLabel')}
            placeholder={t('mobileMapping.fxWorkspace.namePlaceholder')}
            value={name()}
            onInput={(
              event: InputEvent & {
                currentTarget: HTMLInputElement;
              }
            ) => setName(event.currentTarget.value)}
            onKeyDown={handleNameKeyDown}
          />
        }
        primaryBtnLabel={t('mobileMapping.fxWorkspace.createWorkspace')}
        primaryBtnClass={BtnClasses.PRIMARY}
        primaryBtnDisabled={isSaving() || name().trim().length === 0}
        secondaryBtnLabel={t('general.cancel')}
        secondaryBtnClass={BtnClasses.SECONDARY}
        secondaryBtnDisabled={isSaving()}
        onPrimaryAction={() => void handleCreate()}
        onSecondaryAction={handleClose}
        onClose={handleClose}
      />
    </Portal>
  );
};
